/**
 * OnboardingGuard
 *
 * Wraps protected app routes. On mount, it checks UserPreferences to see if
 * onboarding has been completed. When NOT completed, redirects to
 * /app/onboarding so first-time advisors land in the wizard.
 *
 * Rule 12: Guards against useEffect race conditions by tracking isLoading
 * before making any navigation decisions.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import userPreferencesService from '../../services/UserPreferencesService.js';

const OnboardingGuard = ({ children }) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [onboardingCompleted, setOnboardingCompleted] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const prefs = await userPreferencesService.getPreferences();
        if (cancelled) return;
        setOnboardingCompleted(Boolean(prefs?.onboardingCompleted));
      } catch {
        // Fail open — don't lock user out on network/service error
        if (!cancelled) setOnboardingCompleted(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600" />
      </div>
    );
  }

  // If user is already on the onboarding page, don't redirect to avoid loops
  const isOnOnboardingPage = location.pathname.startsWith('/app/onboarding');

  if (!onboardingCompleted && !isOnOnboardingPage) {
    return <Navigate to="/app/onboarding" replace state={{ from: location }} />;
  }

  return children;
};

OnboardingGuard.propTypes = {
  children: PropTypes.node,
};

export default OnboardingGuard;
